import React, { useEffect, useId, useState } from "react";
import classNames from "classnames";
import { FieldPath, useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Markdown } from "component-library";
import { LocalizedText } from "@shared/praxis-config";
import { LocalizedTextValues } from "./config-form-values";
import type { PraxisConfigFormShape } from "./praxis-config-fields";

type Preview = { text: string; isDefault: boolean };

/**
 * The texts as the questionnaire shows them: an own text replaces the default in all
 * languages and the English text falls back to the German one
 */
const resolvePreview = (
  value: LocalizedTextValues | undefined,
  fallback?: LocalizedText,
): { de: Preview; en: Preview } => {
  const de = value?.de.trim() ?? "";
  const en = value?.en.trim() ?? "";

  if (de) {
    return {
      de: { text: de, isDefault: false },
      en: { text: en || de, isDefault: false },
    };
  }
  return {
    de: { text: fallback?.de ?? "", isDefault: true },
    en: { text: fallback?.en ?? fallback?.de ?? "", isDefault: true },
  };
};

const PreviewPanel: React.FC<{ label: string; preview: Preview }> = ({
  label,
  preview,
}) => {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-1">
      <div className="ml-1 flex items-center gap-2 text-base font-semibold">
        {label}
        {preview.isDefault && preview.text && (
          <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
            {t("admin.form.preview.defaultText")}
          </span>
        )}
      </div>
      <div className="flex min-h-40 flex-col gap-2 rounded-lg border border-gray-300 bg-white p-3">
        {preview.text ? (
          <Markdown>{preview.text}</Markdown>
        ) : (
          <p className="text-sm text-gray-500">
            {t("admin.form.preview.empty")}
          </p>
        )}
      </div>
    </div>
  );
};

type Mode = "write" | "preview";

type MarkdownEditorProps = {
  name: FieldPath<PraxisConfigFormShape>;
  labels: { de: string; en: string };
  /** Shown in the preview while no own text is set */
  fallback?: LocalizedText;
  /** Switches back to the input, so the error is visible */
  hasError?: boolean;
};

/**
 * Tabs to switch between the inputs (children) and their rendered Markdown, like on GitHub.
 * The inputs stay mounted while the preview is shown, so react-hook-form keeps them registered.
 */
export const MarkdownEditor: React.FC<
  React.PropsWithChildren<MarkdownEditorProps>
> = ({ name, labels, fallback, hasError, children }) => {
  const { t } = useTranslation();
  const { control } = useFormContext<PraxisConfigFormShape>();
  const [mode, setMode] = useState<Mode>("write");
  const id = useId();
  const value = useWatch({ control, name }) as LocalizedTextValues | undefined;

  useEffect(() => {
    if (hasError) setMode("write");
  }, [hasError]);

  const tabs: { mode: Mode; label: string }[] = [
    { mode: "write", label: t("admin.form.preview.write") },
    { mode: "preview", label: t("admin.form.preview.preview") },
  ];
  const preview = mode === "preview" ? resolvePreview(value, fallback) : null;

  return (
    <div className="flex flex-col">
      <div role="tablist" className="flex gap-1 border-b border-gray-300">
        {tabs.map((tab) => (
          <button
            key={tab.mode}
            type="button"
            role="tab"
            id={`${id}-${tab.mode}-tab`}
            aria-selected={mode === tab.mode}
            aria-controls={`${id}-${tab.mode}`}
            onClick={() => setMode(tab.mode)}
            className={classNames(
              "-mb-px rounded-t-lg border px-4 py-1.5 text-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary",
              mode === tab.mode
                ? "border-gray-300 border-b-white bg-white font-semibold text-gray-900"
                : "border-transparent text-gray-600 hover:text-primary",
            )}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div
        role="tabpanel"
        id={`${id}-write`}
        aria-labelledby={`${id}-write-tab`}
        hidden={mode !== "write"}
        className="pt-3"
      >
        {children}
      </div>

      {preview && (
        <div
          role="tabpanel"
          id={`${id}-preview`}
          aria-labelledby={`${id}-preview-tab`}
          className="grid gap-3 pt-3 md:grid-cols-2"
        >
          <PreviewPanel label={labels.de} preview={preview.de} />
          <PreviewPanel label={labels.en} preview={preview.en} />
        </div>
      )}
    </div>
  );
};
